import { container, injectable } from 'tsyringe';
import { AccountInfo } from '@azure/msal-browser';
import { SiteFeature } from '@gn-audio/core/api/site-settings/enums/site-feature.enum';
import { fetchEcomSetting } from '@gn-audio/core/api/site-settings/fetch-ecom-setting';
import { getInitialsFromAccount, getTitleFromAccount, getWebStoreApiClient, initMavenoidApiClient } from '@gn-audio/core/auth';
import { CssClass, TopMenuEvent } from '@gn-audio/core/common/enums';
import { createElement } from '@gn-audio/core/common/templating';
import { getTopMenuElement, isSiteFeatureEnabled } from '@gn-audio/core/common/utilities';
import { DiToken } from '@gn-audio/sfcc-basket/enums/di-token.enum';
import { IBasketStore } from '@gn-audio/sfcc-basket/models/basket-store';
import { BasketStore } from '@gn-audio/sfcc-basket/services';
import { saveBasketToStorage } from '@gn-audio/sfcc-basket/utilities';
import { getBasketTotalCount } from '@gn-audio/sfcc-basket/utilities/get-basket-total-count';

import { UipMenuComponent } from '../uip-menu/uip-menu.component';

import './uip-icon.component.scss';

import template from './uip-icon.component.html';

@injectable()
export class UipIconComponent {
  private element?: Element;
  private iconElement?: HTMLElement;
  private menuComponent?: UipMenuComponent;

  public render() {
    const containerSeletor = '.top-menu__controls';
    const containerElement: Element | null = document.querySelector(containerSeletor);
    if (!containerElement) {
      console.warn(`Unable to bootstrap UIP widget. Container '${containerSeletor}' is absent.`);
      return;
    }

    this.element = createElement(template, {});
    containerElement.insertAdjacentElement('afterbegin', this.element);
    this.iconElement = this.element.querySelector('.uip-item__icon');
    this.applyAccountInfo();

    initMavenoidApiClient();
    getTopMenuElement()
        .dispatchEvent(new CustomEvent(TopMenuEvent.RegisterCustomTopMenuItem, { detail: this.element }));
  }

  public destroy() {
    this.menuComponent?.destroy();
    this.element?.removeEventListener('click', this.onClick);
    this.element?.remove();
  }

  /**
   * Applies account information on DOM elements if applicable.
   */
  private async applyAccountInfo() {
    await getWebStoreApiClient().onTokenAcquired;
    this.element.addEventListener('click', this.onClick);
    const account: AccountInfo | null = getWebStoreApiClient().account;
    if (!account) {
      return;
    }

    this.iconElement.dataset.initials = getInitialsFromAccount(account);
    this.iconElement.title = getTitleFromAccount(account);
    this.iconElement.classList.add(CssClass.Authorized);

    // Render menu on when token is received and account exists.
    this.menuComponent = container.resolve(UipMenuComponent);
    await this.menuComponent.render(this.iconElement.parentElement);
  }

  private onClick = async () => {
    const account: AccountInfo | null = getWebStoreApiClient().account;
    if (!account) {
      const isEcomEnabled: boolean = !!(await fetchEcomSetting())?.ecomEnabled;
      // In case Ecom and SFCC basket plugin is enabled we should transfer guest basket to authorized user
      if (isEcomEnabled) {
        const isSfccBasketEnabled = await isSiteFeatureEnabled(SiteFeature.SfccBasket);
        if (isSfccBasketEnabled) {
          const basketStore: IBasketStore = container.resolve(BasketStore);
          const totalCount: number = getBasketTotalCount(basketStore.state);
          // Transfer basket only if it's not empty.
          if (totalCount > 0) {
            const storage: Storage = container.resolve(DiToken.Storage);
            // Saves basket state before redirecting to login page
            saveBasketToStorage(storage, await getWebStoreApiClient().onTokenAcquired, basketStore.state);
          }
        }
      }

      getWebStoreApiClient()
          .signIn();
    } else {
      // Checking whether menu is rendered for authorized customer
      // That could happen if user signs in in one tab and clicks on the icon in another one.
      if (!this.menuComponent) {
        await this.applyAccountInfo();
      }
    }
  };
}
